import { useState } from 'react';
import CashDrawerStatusIndicator from './CashDrawerStatusIndicator';
import DrawerDetailsModal from './DrawerDetailsModal';
import OpenDrawerModal from './OpenDrawerModal';
import PaidInOutModal from './PaidInOutModal';
import ChangeCashierModal from './ChangeCashierModal';
import ClosingBalanceModal from './ClosingBalanceModal';

export default function Header() {
  const [showDrawerDetails, setShowDrawerDetails] = useState(false);
  const [showOpenDrawer, setShowOpenDrawer] = useState(false);
  const [showPaidInOut, setShowPaidInOut] = useState(false);
  const [paidInOutType, setPaidInOutType] = useState<'in' | 'out'>('in');
  const [showChangeCashier, setShowChangeCashier] = useState(false);
  const [showClosingBalance, setShowClosingBalance] = useState(false);
  const [currentCashier, setCurrentCashier] = useState('Sarah Johnson');

  const handlePaidIn = () => {
    setPaidInOutType('in');
    setShowPaidInOut(true);
  };

  const handlePaidOut = () => {
    setPaidInOutType('out');
    setShowPaidInOut(true);
  };

  const handleCheckOut = () => {
    setShowClosingBalance(true);
  };

  const handleCashierChange = (cashierName: string) => {
    setCurrentCashier(cashierName);
    setShowChangeCashier(false);
  };

  const handlePaidInOutConfirm = (amount: number, reason: string, note: string) => {
    // Save the paid in/out transaction
    const transaction = {
      type: paidInOutType,
      amount,
      reason,
      note,
      timestamp: new Date().toISOString(),
      cashier: currentCashier,
    };
    
    // Store in session storage
    const existingTransactions = JSON.parse(sessionStorage.getItem('paidInOutTransactions') || '[]');
    existingTransactions.push(transaction);
    sessionStorage.setItem('paidInOutTransactions', JSON.stringify(existingTransactions));
    
    setShowPaidInOut(false);
  };

  // Calculate current balance from opening balance and transactions
  const calculateCurrentBalance = () => {
    const checkInData = sessionStorage.getItem('posCheckInData');
    const openingBalanceData = sessionStorage.getItem('posOpeningBalance');
    
    let balance = 0;
    
    if (openingBalanceData) {
      const openingData = JSON.parse(openingBalanceData);
      balance = parseFloat(openingData.amount) || 0;
    }
    
    // Add paid in/out transactions
    const transactions = JSON.parse(sessionStorage.getItem('paidInOutTransactions') || '[]');
    transactions.forEach((transaction: any) => {
      if (transaction.type === 'in') {
        balance += transaction.amount;
      } else {
        balance -= transaction.amount;
      }
    });
    
    return balance;
  };

  // Mock drawer data
  const drawerInfo = {
    store: 'Downtown Store',
    lane: 'Lane 3',
    cashier: 'Sarah Johnson',
    shift: 'Morning Shift',
    status: 'open' as const,
    openingBalance: 200.00,
    cashSales: 1245.50,
    cashRefunds: 45.00,
    paidIn: 100.00,
    paidOut: 50.00,
    expectedBalance: 1450.50,
  };

  // Mock events data
  const events = [
    {
      id: '1',
      timestamp: '2024-01-15 08:00:00',
      type: 'auto_open' as const,
      user: 'Sarah Johnson',
      role: 'Cashier',
      amount: 200.00,
      note: 'Shift started with opening balance',
    },
    {
      id: '2',
      timestamp: '2024-01-15 10:30:00',
      type: 'paid_in' as const,
      user: 'Sarah Johnson',
      role: 'Cashier',
      amount: 100.00,
      reason: 'Change float replenishment',
      note: 'Added small bills for change',
    },
    {
      id: '3',
      timestamp: '2024-01-15 12:15:00',
      type: 'paid_out' as const,
      user: 'Sarah Johnson',
      role: 'Cashier',
      amount: 50.00,
      reason: 'Petty cash expense',
      note: 'Office supplies purchase',
    },
  ];

  return (
    <>
      <header className="bg-gradient-to-r from-gray-900 to-gray-800 text-white px-6 py-3 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-teal-600 rounded-lg">
              <i className="ri-store-2-line text-2xl"></i>
            </div>
            <div>
              <h1 className="text-xl font-bold whitespace-nowrap">RetailPro POS</h1>
              <p className="text-xs text-gray-300 whitespace-nowrap">Point of Sale System</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <CashDrawerStatusIndicator 
            onViewDetails={() => setShowDrawerDetails(true)}
          />

          <div className="flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-2">
            <i className="ri-user-line text-lg"></i>
            <div className="text-left">
              <p className="text-xs text-gray-400 whitespace-nowrap">Cashier</p>
              <p className="text-sm font-semibold whitespace-nowrap">{currentCashier}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-2">
            <i className="ri-store-line text-lg"></i>
            <div className="text-left">
              <p className="text-xs text-gray-400 whitespace-nowrap">Lane 3</p>
              <p className="text-sm font-semibold whitespace-nowrap">Downtown Store</p>
            </div>
          </div>

          <div className="flex gap-2">
            <button 
              onClick={() => setShowOpenDrawer(true)}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
              title="Open Cash Drawer"
            >
              <i className="ri-inbox-line mr-1"></i>
              Open Drawer
            </button>

            <button 
              onClick={handlePaidIn}
              className="px-3 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
              title="Paid In - Add cash to drawer"
            >
              <i className="ri-arrow-down-circle-line mr-1"></i>
              Paid In
            </button>

            <button 
              onClick={handlePaidOut}
              className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
              title="Paid Out - Remove cash from drawer"
            >
              <i className="ri-arrow-up-circle-line mr-1"></i>
              Paid Out
            </button>

            <button 
              onClick={() => setShowChangeCashier(true)}
              className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
              title="Change Cashier"
            >
              <i className="ri-user-shared-line mr-1"></i>
              Change Cashier
            </button>

            <button 
              onClick={handleCheckOut}
              className="px-3 py-2 bg-orange-600 hover:bg-orange-700 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
              title="Check Out - Close shift and reconcile cash"
            >
              <i className="ri-logout-box-line mr-1"></i>
              Check Out
            </button>
          </div>
        </div>
      </header>

      {showDrawerDetails && (
        <DrawerDetailsModal 
          onClose={() => setShowDrawerDetails(false)}
          drawerInfo={drawerInfo}
          events={events}
        />
      )}

      {showOpenDrawer && (
        <OpenDrawerModal onClose={() => setShowOpenDrawer(false)} />
      )}

      {showPaidInOut && (
        <PaidInOutModal 
          type={paidInOutType}
          onClose={() => setShowPaidInOut(false)}
          onConfirm={handlePaidInOutConfirm}
          userRole="supervisor"
          currentBalance={calculateCurrentBalance()}
        />
      )}

      {showChangeCashier && (
        <ChangeCashierModal 
          onClose={() => setShowChangeCashier(false)}
          currentCashier={currentCashier}
          onCashierChange={handleCashierChange}
        />
      )}

      {showClosingBalance && (
        <ClosingBalanceModal onClose={() => setShowClosingBalance(false)} />
      )}
    </>
  );
}
